import { useEffect, useRef, useState } from "react";
import { AlertCircle, Undo2, X } from "lucide-react";
import {
  pdfBase64ToObjectUrl,
  timetableDataQualityApi,
  type TeacherContextEntry,
  type TeacherContextPdf,
  type TeacherContextResponse,
} from "@/services/api/timetableDataQuality";
import { ApiErrorResponse } from "@/services/api/client";
import { extractErrorMessage } from "@/utils/errorHandler";
import {
  formatPeriodLabel,
  groupEntriesByPeriod,
  isPeriodCurrent,
} from "@/utils/timetableGrouping";
import {
  occupiedDays,
  occupiedSlotLabels,
  orderedSlotLabels,
  slotSpanLabels,
  toGridEntry,
  weekDaysForEntries,
} from "@/utils/teacherContextGrid";
import { TimetableGridCSS } from "@/components/timetable-import/TimetableGridCSS";
import { useEntryFieldRebind } from "@/components/dataquality/useEntryFieldRebind";
import { GapCellPopover } from "@/components/dataquality/GapCellPopover";
import {
  EvidencePdfPane,
  type PdfViewPersist,
} from "@/components/dataquality/EvidencePdfPane";
import { SystemFitBar, ZoomSlider } from "@/components/dataquality/ZoomSlider";
import { FittedScrollPane } from "@/components/dataquality/FittedScrollPane";
import {
  flushPersonViews,
  queuePersonView,
  resolvePersonView,
  writePersonView,
  type PersonView,
} from "@/components/dataquality/personViewMemory";
import {
  adjacentSlot,
  fusionNeighbor,
  type SchoolSlot,
} from "@/utils/spanNeighbor";

interface SystemTimetableModalProps {
  teacherId: string;
  batchId?: string | null;
  onFindingClick?: (entryId: string) => boolean;
  onPlanConfirmed?: (teacherId: string, confirmed: boolean) => void;
  onClose: () => void;
}

export function SystemTimetableModal({
  teacherId,
  batchId,
  onPlanConfirmed,
  onClose,
}: SystemTimetableModalProps) {
  const [context, setContext] = useState<TeacherContextResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [personView, setPersonView] = useState<PersonView>(() =>
    resolvePersonView(teacherId)
  );
  const viewTeacherRef = useRef(teacherId);
  if (viewTeacherRef.current !== teacherId) {
    flushPersonViews();
    viewTeacherRef.current = teacherId;
    setPersonView(resolvePersonView(teacherId));
  }
  const storedView = resolvePersonView(teacherId);
  const liveView: PersonView = {
    ...storedView,
    ...personView,
    systemScrollLeft: storedView.systemScrollLeft,
    systemScrollTop: storedView.systemScrollTop,
    pdfScrollLeft: storedView.pdfScrollLeft,
    pdfScrollTop: storedView.pdfScrollTop,
  };
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfMeta, setPdfMeta] = useState<TeacherContextPdf | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [planConfirmed, setPlanConfirmed] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [popover, setPopover] = useState<{
    entry: TeacherContextEntry;
    periodEntries: TeacherContextEntry[];
    anchor: DOMRect;
  } | null>(null);
  const [extendBusy, setExtendBusy] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [undoStack, setUndoStack] = useState<
    Array<
      { type: "extend"; undoId: string } | { type: "delete"; entryId: string }
    >
  >([]);
  const pdfUrlRef = useRef<string | null>(null);
  const pdfOwnerRef = useRef(teacherId);
  if (pdfOwnerRef.current !== teacherId) {
    pdfOwnerRef.current = teacherId;
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    setPdfUrl(null);
    setPdfMeta(null);
    setPdfLoading(true);
  }
  const rebind = useEntryFieldRebind(() => {
    setPopover(null);
    setReloadKey((n) => n + 1);
  });

  useEffect(
    () => () => {
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    },
    []
  );

  useEffect(
    () => () => {
      flushPersonViews();
    },
    []
  );

  const pdfView: PdfViewPersist = {
    scale: liveView.pdfScale,
    scrollLeft: liveView.pdfScrollLeft,
    scrollTop: liveView.pdfScrollTop,
  };

  const updatePersonView = (patch: Partial<PersonView>) => {
    setPersonView(writePersonView(teacherId, patch));
  };
  const queueScroll = (patch: Partial<PersonView>) => {
    queuePersonView(teacherId, patch);
  };

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setContext(null);
    setPdfError(null);
    setPdfMeta(null);
    setPdfLoading(true);
    void (async () => {
      try {
        const result = await timetableDataQualityApi.getTeacherContext(
          teacherId,
          batchId ?? undefined
        );
        if (cancelled) return;
        setContext(result);
        setPlanConfirmed(Boolean(result.teacher.manually_confirmed));
        const pdf = result.pdf;
        setPdfMeta(pdf);
        if (!pdf?.page) {
          const prev = pdfUrlRef.current;
          pdfUrlRef.current = null;
          setPdfUrl(null);
          if (prev) URL.revokeObjectURL(prev);
          setPdfLoading(false);
          return;
        }
        try {
          const page = await timetableDataQualityApi.getPdfPage(
            pdf.file_id,
            pdf.page
          );
          if (cancelled) return;
          if (page.page_number !== pdf.page) {
            setPdfLoading(false);
            return;
          }
          const url = pdfBase64ToObjectUrl(page.pdf_base64);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          const prev = pdfUrlRef.current;
          pdfUrlRef.current = url;
          setPdfUrl(url);
          if (prev) URL.revokeObjectURL(prev);
          setPdfLoading(false);
        } catch (err) {
          if (cancelled) return;
          setPdfLoading(false);
          if (err instanceof ApiErrorResponse && err.statusCode === 422) return;
          setPdfError(
            extractErrorMessage(err, {
              loadError: "PDF konnte nicht geladen werden",
            })
          );
        }
      } catch (err) {
        if (!cancelled) {
          setPdfLoading(false);
          setError(
            extractErrorMessage(err, {
              loadError: "Stundenplan konnte nicht geladen werden",
            })
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, batchId, reloadKey]);

  const periods = context ? groupEntriesByPeriod(context.entries) : [];
  const dayNumbers = context?.entries.map((entry) => entry.day_of_week) ?? [];
  const gridDays =
    liveView.systemFit === "fixed-width"
      ? occupiedDays(dayNumbers)
      : weekDaysForEntries(dayNumbers);
  const gridSlots =
    liveView.systemFit === "height"
      ? occupiedSlotLabels(context?.entries ?? [], context?.slots)
      : liveView.systemFit === "fit"
        ? slotSpanLabels(context?.entries ?? [], context?.slots)
        : orderedSlotLabels(context?.entries ?? [], context?.slots);

  const schoolSlots: SchoolSlot[] = context?.slots ?? [];

  const neighborFor = (
    entry: TeacherContextEntry,
    entries: TeacherContextEntry[],
    direction: "up" | "down"
  ) => {
    return fusionNeighbor(entries, entry, direction, schoolSlots);
  };

  const extendDouble = async (direction: "up" | "down") => {
    if (!popover || extendBusy) return;
    setExtendBusy(true);
    setExtendError(null);
    try {
      const result = await timetableDataQualityApi.extendDouble({
        entry_id: popover.entry.id,
        direction,
      });
      if (result.undo_id) {
        setUndoStack((prev) => [
          ...prev,
          { type: "extend", undoId: result.undo_id! },
        ]);
      }
      setPopover(null);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setExtendError(
        extractErrorMessage(err, { loadError: "Verbinden fehlgeschlagen" })
      );
    } finally {
      setExtendBusy(false);
    }
  };

  const deleteEntry = async () => {
    if (!popover || extendBusy) return;
    setExtendBusy(true);
    setExtendError(null);
    try {
      await timetableDataQualityApi.deleteEntry(popover.entry.id);
      setUndoStack((prev) => [
        ...prev,
        { type: "delete", entryId: popover.entry.id },
      ]);
      setPopover(null);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setExtendError(
        extractErrorMessage(err, { loadError: "Löschen fehlgeschlagen" })
      );
    } finally {
      setExtendBusy(false);
    }
  };

  const undoLast = async () => {
    const last = undoStack[undoStack.length - 1];
    if (!last || extendBusy) return;
    setExtendBusy(true);
    setExtendError(null);
    try {
      if (last.type === "delete") {
        await timetableDataQualityApi.restoreEntry(last.entryId);
      } else {
        if (!last.undoId) throw new Error("Rückgängig nicht verfügbar");
        await timetableDataQualityApi.undoExtend({ undo_id: last.undoId });
      }
      setUndoStack((prev) => prev.slice(0, -1));
      setPopover(null);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setExtendError(
        extractErrorMessage(err, { loadError: "Rückgängig fehlgeschlagen" })
      );
    } finally {
      setExtendBusy(false);
    }
  };

  const togglePlanConfirmed = async () => {
    if (reviewBusy) return;
    const next = !planConfirmed;
    setReviewBusy(true);
    setReviewError(null);
    try {
      await timetableDataQualityApi.setTeacherReview({
        category: "timetable",
        value: null,
        is_null_value: true,
        teacher_id: teacherId,
        confirmed: next,
      });
      setPlanConfirmed(next);
      onPlanConfirmed?.(teacherId, next);
      if (next) onClose();
    } catch (err) {
      setReviewError(
        extractErrorMessage(err, { loadError: "Bestätigung fehlgeschlagen" })
      );
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-1">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="system-timetable-title"
        className="bg-white rounded-lg w-[98vw] h-[98vh] max-w-none flex flex-col"
      >
        <div className="px-3 py-2 flex items-center justify-between shrink-0 border-b border-gray-100">
          <div>
            <h3 id="system-timetable-title" className="text-lg font-semibold">
              Stundenplan: {context?.teacher.name ?? "…"}
              {context?.teacher.code && (
                <span className="ml-2 font-mono text-sm text-gray-500">
                  {context.teacher.code}
                </span>
              )}
            </h3>
            {context && (
              <p className="text-sm text-gray-500 mt-0.5">
                {context.entries.length} Einträge · {periods.length}{" "}
                {periods.length === 1 ? "Periode" : "Perioden"} · Badge klicken
                zum Bearbeiten
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void undoLast()}
              disabled={undoStack.length === 0 || extendBusy}
              className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-40"
            >
              <Undo2 className="w-4 h-4" />
              Rückgängig
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Schliessen"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-0 flex-1 overflow-hidden">
          <div className="flex h-full min-h-0 flex-col overflow-hidden p-2">
            <div className="flex items-center justify-between gap-2 mb-1 shrink-0">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                System-Stundenplan
              </h5>
              <div className="flex flex-col items-end gap-1">
                <SystemFitBar
                  value={liveView.systemFit}
                  onChange={(systemFit) => updatePersonView({ systemFit })}
                />
                <ZoomSlider
                  value={liveView.systemZoom}
                  onChange={(systemZoom) => updatePersonView({ systemZoom })}
                  label="System-Zoom"
                />
              </div>
            </div>
            <button
              type="button"
              disabled={reviewBusy}
              onClick={() => void togglePlanConfirmed()}
              className={`mb-2 shrink-0 w-full px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${
                planConfirmed
                  ? "bg-green-100 text-green-800 border border-green-300 hover:bg-green-50"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
            >
              {planConfirmed
                ? "Manuell bestätigt — Klick hebt auf"
                : "Manuell bestätigt"}
            </button>
            {(reviewError || (!popover && extendError)) && (
              <p className="text-sm text-red-600 mb-2">
                {reviewError || extendError}
              </p>
            )}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {error ? (
                <div className="flex items-center justify-center py-10 text-red-600 text-sm">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {error}
                </div>
              ) : !context ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  Lade Stundenplan…
                </div>
              ) : periods.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  Keine Stundenplan-Einträge für diesen Lehrer vorhanden.
                </div>
              ) : (
                <>
                  {rebind.loadError && (
                    <div className="flex items-center text-red-600 text-sm mb-2">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {rebind.loadError}
                    </div>
                  )}
                  <FittedScrollPane
                    resetKey={teacherId}
                    zoom={liveView.systemZoom}
                    fitMode={liveView.systemFit}
                    scrollLeft={liveView.systemScrollLeft}
                    scrollTop={liveView.systemScrollTop}
                    onScroll={(systemScrollLeft, systemScrollTop) => {
                      queueScroll({ systemScrollLeft, systemScrollTop });
                    }}
                    onWheelZoom={(systemZoom) =>
                      updatePersonView({ systemZoom })
                    }
                    className="flex-1"
                  >
                    <div className="flex w-max flex-col gap-3">
                      {periods.map((period, idx) => {
                        const current = isPeriodCurrent(
                          period.validFrom,
                          period.validTo
                        );
                        return (
                          <div
                            key={idx}
                            className="w-max overflow-visible border border-gray-200 rounded-lg"
                          >
                            <div
                              className={`px-4 py-2 flex items-center gap-2 shrink-0 ${
                                current ? "bg-green-50" : "bg-gray-50"
                              }`}
                            >
                              <span
                                className={`text-sm font-medium ${current ? "text-green-700" : "text-gray-600"}`}
                              >
                                {formatPeriodLabel(
                                  period.validFrom,
                                  period.validTo
                                )}
                              </span>
                              {current && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                  Aktuell
                                </span>
                              )}
                              <span className="text-xs text-gray-500">
                                ({period.entries.length} Einträge)
                              </span>
                            </div>
                            <div className="p-2">
                              <TimetableGridCSS
                                entries={period.entries.map((entry) => {
                                  const g = toGridEntry(entry);
                                  if (planConfirmed) g.ampel = "green";
                                  return g;
                                })}
                                onCellClick={(
                                  _day,
                                  _slot,
                                  indices,
                                  event,
                                  field
                                ) => {
                                  const entry =
                                    indices[0] !== undefined
                                      ? period.entries[indices[0]]
                                      : null;
                                  if (!entry) return;
                                  if (field) {
                                    setPopover(null);
                                    void rebind.open(entry.id, field, event);
                                    return;
                                  }
                                  const el = event?.currentTarget as
                                    HTMLElement | undefined;
                                  setExtendError(null);
                                  setPopover({
                                    entry,
                                    periodEntries: period.entries,
                                    anchor:
                                      el?.getBoundingClientRect() ??
                                      new DOMRect(24, 120, 80, 40),
                                  });
                                }}
                                onCellDoubleClick={() => {}}
                                highlightedCell={null}
                                highlightedEntryIndices={
                                  popover
                                    ? new Set(
                                        period.entries.findIndex(
                                          (e) => e.id === popover.entry.id
                                        ) >= 0
                                          ? [
                                              period.entries.findIndex(
                                                (e) => e.id === popover.entry.id
                                              ),
                                            ]
                                          : []
                                      )
                                    : new Set()
                                }
                                fitToView={false}
                                intrinsic
                                fixedDays={
                                  gridDays.length > 0 ? gridDays : undefined
                                }
                                fixedTimeSlots={
                                  gridSlots.length > 0 ? gridSlots : undefined
                                }
                                includeEmptyEntries
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </FittedScrollPane>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col min-h-0 p-2 border-t md:border-t-0 md:border-l border-gray-100">
            <div className="flex items-center justify-between gap-2 mb-1 shrink-0">
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                PDF-Stundenplan
              </h5>
              <ZoomSlider
                value={pdfView.scale}
                onChange={(pdfScale) => updatePersonView({ pdfScale })}
                label="PDF-Zoom"
              />
            </div>
            {pdfMeta && (
              <p className="text-xs text-gray-500 mb-1 shrink-0">
                {pdfMeta.original_filename ?? "PDF"}
                {pdfMeta.page
                  ? ` · Seite ${pdfMeta.page}${pdfMeta.source === "bulk_mapping" ? " (Bulk-Mapping)" : ""}`
                  : ""}
              </p>
            )}
            <div className="flex-1 min-h-0 border border-gray-200 rounded bg-gray-50 flex flex-col">
              {pdfUrl ? (
                <EvidencePdfPane
                  url={pdfUrl}
                  view={pdfView}
                  onViewChange={(next) => {
                    if (next.scrollLeft != null || next.scrollTop != null) {
                      queueScroll({
                        pdfScrollLeft:
                          next.scrollLeft ?? liveView.pdfScrollLeft,
                        pdfScrollTop: next.scrollTop ?? liveView.pdfScrollTop,
                      });
                    }
                    if (next.scale != null)
                      updatePersonView({ pdfScale: next.scale });
                  }}
                />
              ) : pdfLoading || (!context && !error) ? (
                <p className="text-sm text-gray-500 p-4">Lade PDF…</p>
              ) : pdfError ? (
                <p className="text-sm text-red-600 p-4">{pdfError}</p>
              ) : !pdfMeta ? (
                <p className="text-sm text-gray-500 p-4">
                  Kein PDF-Stundenplan vorhanden (weder Import-Mapping noch
                  Upload der Kategorie „Stundenplan“).
                </p>
              ) : (
                <p className="text-sm text-gray-500 p-4">
                  Seite unbekannt — das Sammel-PDF wird nicht geöffnet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {rebind.panel}
      {popover && (
        <GapCellPopover
          entry={popover.entry}
          neighborUp={neighborFor(
            popover.entry,
            context?.entries ?? popover.periodEntries,
            "up"
          )}
          neighborDown={neighborFor(
            popover.entry,
            context?.entries ?? popover.periodEntries,
            "down"
          )}
          canExtendUp={
            neighborFor(
              popover.entry,
              context?.entries ?? popover.periodEntries,
              "up"
            ) != null ||
            adjacentSlot(schoolSlots, popover.entry.time_slot_id, "up") !== null
          }
          canExtendDown={
            neighborFor(
              popover.entry,
              context?.entries ?? popover.periodEntries,
              "down"
            ) != null ||
            adjacentSlot(schoolSlots, popover.entry.time_slot_id, "down") !==
              null
          }
          anchor={popover.anchor}
          busy={extendBusy}
          error={extendError}
          onExtend={(direction) => void extendDouble(direction)}
          onDelete={() => void deleteEntry()}
          onEditField={(field) => {
            const entryId = popover.entry.id;
            const anchorEl = {
              currentTarget: { getBoundingClientRect: () => popover.anchor },
            };
            setPopover(null);
            void rebind.open(entryId, field, anchorEl);
          }}
          onClose={() => {
            if (!extendBusy) setPopover(null);
          }}
        />
      )}
    </div>
  );
}
